// v4 idiomatic recompose of src/pages/help/Help.tsx — static Zendesk article
// links grouped by topic, role/partner-gated exactly like v3. SEB app type
// still gets the "coming soon" stub (v3 parity — the embedded SEB browser
// doesn't render an external-link list usefully).
// Docs-style layout (BB-feedback4): sticky jump-nav (lg+) beside a single
// readable column of stacked sections — all topics scannable at once.
import { useCurrentUser } from '@/hooks/dataHelpers';
import { getAppType } from '@/lib/offline/OfflineAppHelper';
import { selectPartnerName, useCommonStore } from '@/store/commonStore';
import { ArrowRightUpLinear, QuestionCircleLinear } from '@assessprep/icons/solar';
import { useTranslation } from 'react-i18next';

interface HelpArticle {
  href: string;
  labelKey: string;
}

interface HelpSection {
  key: string;
  labelKey: string;
  visible: boolean;
  articles: HelpArticle[];
}

const HelpLink = ({ article }: { article: HelpArticle }) => {
  const { t } = useTranslation();
  return (
    <a
      href={article.href}
      target="_blank"
      rel="noopener noreferrer"
      className="group -mx-2 flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-sm text-foreground/90 outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-[3px] focus-visible:ring-ap-ring-a50"
    >
      <span className="min-w-0 break-words">{t(article.labelKey)}</span>
      <ArrowRightUpLinear
        aria-hidden="true"
        className="size-4 shrink-0 text-muted-foreground transition-colors group-hover:text-accent-foreground rtl:-scale-x-100"
      />
    </a>
  );
};

export const HelpPage = () => {
  const { t } = useTranslation();
  const appType = getAppType();
  const partnerName = useCommonStore(selectPartnerName);
  const currentUser = useCurrentUser();
  const isStudentOrParent = currentUser?.role === 'student' || currentUser?.role === 'parent';
  const notPartner21k = partnerName !== '21k';

  const sections: HelpSection[] = [
    {
      key: 'guide-7',
      labelKey: 'Help.getting_started_with_assessprep',
      visible: notPartner21k && !isStudentOrParent,
      articles: [
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841301325713-Setup-for-a-Non-MB-school',
          labelKey: 'Help.setup_for_a_nonmb_schools',
        },
      ],
    },
    {
      key: 'guide-1',
      labelKey: 'Help.creating_an_assessment',
      visible: !isStudentOrParent,
      articles: [
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4831181980177-Creating-a-test-on-AssessPrep',
          labelKey: 'Help.creating_a_test_on_assessprep',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841224337809-Accessing-editing-and-duplicating-tests',
          labelKey: 'Help.accessing_editing_and_duplicating_tests',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4831275244049-Content-types-on-AssessPrep',
          labelKey: 'Help.content_types_on_assessprep',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841222799633-Question-types-on-AssessPrep',
          labelKey: 'Help.question_types_on_assessprep',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/5465815913361-Rearrange-adding-collaborators-and-other-actions',
          labelKey: 'Help.rearrange_adding_collaborators_and_other_actions',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841228082065-Using-the-AssessPrep-Library-Test-Banks-and-more',
          labelKey: 'Help.using_the_assessprep_library_test_banks_and_more',
        },
      ],
    },
    {
      key: 'guide-2',
      labelKey: 'Help.delivery_settings',
      visible: !isStudentOrParent,
      articles: [
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/6058182285201-Publishing-and-Closing-access-to-a-test',
          labelKey: 'Help.publishing_and_closing_access_to_a_test',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841245203601-Delivery-modes',
          labelKey: 'Help.delivery_modes',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841268533777-Start-condition-Scheduling-a-test-vs-access-with-join-code',
          labelKey: 'Help.start_condition_scheduling_a_test_vs_access_with_join_code',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841268935825-Test-delivery-settings',
          labelKey: 'Help.test_delivery_settings',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841254523537-Mapping-Students-to-a-test',
          labelKey: 'Help.mapping_students_to_a_test',
        },
      ],
    },
    {
      key: 'guide-5',
      labelKey: 'Help.monitoring_a_test',
      visible: !isStudentOrParent,
      articles: [
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/5601640089745-Monitoring-an-assessment-as-an-Invigilator-',
          labelKey: 'Help.monitoring_an_assessment_as_an_invigilator',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841247686033-Monitoring-Students-using-the-Live-Invigilation-Dashboard',
          labelKey: 'Help.monitoring_students_using_the_live_invigilation_dashboard',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841239101329-Monitor-Remotely-via-Zoom-Microsoft-Teams-or-Google-Meet',
          labelKey: 'Help.monitor_remotely_via_zoom_microsoft_teams_or_google_meet',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841271564689-Monitor-Remotely-via-embedded-AP-video-call',
          labelKey: 'Help.monitor_remotely_via_embedded_ap_video_call',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841239606033-For-Admins-Monitoring-all-assessments-in-your-school',
          labelKey: 'Help.for_admins_monitoring_all_assessments_in_your_school',
        },
      ],
    },
    {
      key: 'guide-4',
      labelKey: 'Help.grading_and_analytics',
      visible: !isStudentOrParent,
      articles: [
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841272501521-Grading-student-responses',
          labelKey: 'Help.grading_student_responses',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841272770321-Annotating-student-hand-written-submissions',
          labelKey: 'Help.grading_and_annotating_handwritten_submissions',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841264360465-Understanding-the-grading-grid-view-',
          labelKey: 'Help.understanding_the_grading_gridview',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841276747665-Advanced-tips-and-tricks',
          labelKey: 'Help.advanced_tips_and_tricks',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841284938513-Viewing-results-feedback-as-a-student',
          labelKey: 'Help.viewing_results__feedback_as_a_student',
        },
      ],
    },
    {
      key: 'guide-3',
      labelKey: 'Help.taking_a_test_as_a_student',
      visible: true,
      articles: [
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/6055572318737-Troubleshooting-issues-with-Safe-Exam-Browser-SEB-',
          labelKey: 'Help.troubleshooting_issues_with_safe_exam_browser_seb',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841287667217-Installing-AssessPrep-lockdown-app-for-Windows-Mac-and-iPad',
          labelKey: 'Help.installing_assessprep_lockdown_app_for_windows_mac_and_ipad',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841302240145-Taking-a-test-in-a-browser',
          labelKey: 'Help.taking_a_test_in_a_browser',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841317799825-Taking-a-test-in-lock-down-mode',
          labelKey: 'Help.taking_a_test_in_lockdown_mode',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841279890961-Taking-a-test-as-a-guest-user',
          labelKey: 'Help.taking_a_test_as_a_guest_user',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841280151057-Uploading-hand-written-submissions',
          labelKey: 'Help.uploading_handwritten_submissions',
        },
      ],
    },
    {
      key: 'guide-managebac',
      labelKey: 'Help.integration_with_managebac',
      visible: notPartner21k && !isStudentOrParent,
      articles: [
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841304893585-Integrating-AssessPrep-with-ManageBac',
          labelKey: 'Help.integrating_assessprep_with_managebac',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841291248273-Accessing-AssessPrep-via-ManageBac-Single-Sign-On',
          labelKey: 'Help.accessing_assessprep_via_managebac_single_signon',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841282551569-Creating-an-AssessPrep-Online-Assessment-using-a-ManageBac-Task',
          labelKey: 'Help.creating_an_assessprep_online_assessment_using_a_managebac_task',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841291772945-Linking-an-Existing-AssessPrep-Online-Assessment-to-a-ManageBac-Task',
          labelKey: 'Help.linking_an_existing_assessprep_online_assessment_to_a_managebac_task',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841315848209-Grading-an-AssessPrep-Online-Assessment-via-the-ManageBac-Gradebook',
          labelKey: 'Help.grading_an_assessprep_online_assessment_via_the_managebac_gradebook',
        },
      ],
    },
    {
      key: 'guide-6',
      labelKey: 'Help.troubleshooting_and_faqs',
      visible: true,
      articles: [
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841335022609-FAQs',
          labelKey: 'CommonText.faqs',
        },
        {
          href: 'https://assessprep.zendesk.com/hc/en-us/articles/4841317260177-Common-Technical-Issues',
          labelKey: 'Help.common_technical_issues',
        },
      ],
    },
  ].filter((section) => section.visible);

  // Root gap-6, not gap-8 — v3's unlayered `.gap-8 { gap: 8px }` (Utilities.scss)
  // would shrink the rhythm to 8px.
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6" data-testid="help-page">
      <div className="flex items-center gap-2">
        <QuestionCircleLinear aria-hidden="true" className="size-6 text-muted-foreground" />
        <h1 className="text-2xl font-semibold tracking-tight">{t('CommonText.help')}</h1>
      </div>

      {appType === 'seb' ? (
        <p className="text-sm text-muted-foreground" data-testid="help-seb-stub">
          {t('CommonText.coming_soon')}
        </p>
      ) : (
        <div className="flex items-start gap-10">
          {/* Jump nav — plain in-page anchors; the shell's scroll container
              handles the jump, scroll-mt on each section keeps headings clear.
              max-lg spelling, not `hidden lg:flex` — v3's unlayered
              `.hidden{!important}` would keep the nav hidden at every viewport. */}
          <nav
            aria-label={t('CommonText.help')}
            className="sticky top-6 flex w-56 shrink-0 flex-col gap-0.5 max-lg:hidden"
            data-testid="help-nav"
          >
            {sections.map((section) => (
              <a
                key={section.key}
                href={`#help-${section.key}`}
                className="rounded-md px-3 py-1.5 text-sm text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-[3px] focus-visible:ring-ap-ring-a50"
                data-testid={`help-nav-${section.key}`}
              >
                {t(section.labelKey)}
              </a>
            ))}
          </nav>

          {/* Article column floats as a card on the canvas (LP1); nav stays chrome. */}
          <div className="flex min-w-0 max-w-prose flex-1 flex-col gap-10 rounded-lg border bg-card p-6 shadow-sm">
            {sections.map((section) => (
              <section
                key={section.key}
                id={`help-${section.key}`}
                className="flex scroll-mt-6 flex-col gap-2"
                data-testid={`help-section-${section.key}`}
              >
                <h2 className="border-b pb-2 text-lg font-semibold tracking-tight">
                  {t(section.labelKey)}
                </h2>
                <ul className="flex flex-col gap-0.5">
                  {section.articles.map((article) => (
                    <li key={article.href}>
                      <HelpLink article={article} />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
